import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/lib/motion";

const CATEGORIES = ["All", "SEO", "Google Ads", "Social Media", "Marketing"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/blog", { params: category === "All" ? {} : { category } })
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <main className="pt-32 pb-24 min-h-screen bg-white" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <span className="overline text-[var(--dm-purple)]">The Journal</span>
          <h1 className="font-head font-extrabold tracking-tighter text-5xl sm:text-6xl text-slate-900 mt-4 leading-[0.95] max-w-3xl">
            Marketing <span className="text-gradient">insights</span> that move the needle
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl">
            Strategies, case studies and trends from the Digital Madhuraa team.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2.5">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-5 py-2 text-sm font-head font-bold transition-colors ${
                category === c ? "text-white" : "text-slate-700 bg-slate-100 hover:bg-slate-200"
              }`}
              style={category === c ? { background: "linear-gradient(135deg,#6C2BD9,#2563EB)" } : {}}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-16 text-center text-slate-400">Loading articles…</div>
        ) : posts.length === 0 ? (
          <div className="mt-16 text-center text-slate-400" data-testid="blog-empty">No articles in this category yet.</div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              >
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="group block rounded-2xl border border-slate-200 overflow-hidden hover:shadow-2xl hover:shadow-slate-200 transition-shadow h-full">
                  <div className="h-52 overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="rounded-full bg-purple-100 text-[var(--dm-purple)] px-3 py-1 text-xs font-head font-bold">{p.category}</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400"><Clock size={13} /> {p.read_time}</span>
                    </div>
                    <h2 className="font-head font-bold text-xl text-slate-900 leading-snug group-hover:text-[var(--dm-purple)] transition-colors">{p.title}</h2>
                    <p className="mt-3 text-slate-600 text-[0.95rem] line-clamp-2">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-head font-bold text-[var(--dm-purple)]">
                      Read article <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
